import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AuthShell } from "@/components/AuthShell";
import { PasswordField, passwordValid } from "@/components/PasswordField";

export default function SetPassword({ purpose }) {
  const nav = useNavigate();
  const tokenRef = useRef(null);
  const [info, setInfo] = useState(null); // null=checking, false=invalid
  const [invalidMsg, setInvalidMsg] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const isInvite = purpose === "invite";

  useEffect(() => {
    // Referrer-Policy: no-referrer + fjern token fra adresselinjen
    const meta = document.createElement("meta"); meta.name = "referrer"; meta.content = "no-referrer"; document.head.appendChild(meta);
    // StrictMode kører effekten to gange – genbrug token fra første kørsel (URL er allerede renset)
    const token = tokenRef.current || new URLSearchParams(window.location.search).get("token") || "";
    tokenRef.current = token;
    window.history.replaceState({}, "", window.location.pathname);
    if (!token) { setInfo(false); setInvalidMsg("Linket mangler en nøgle. Bed om et nyt link."); return () => meta.remove(); }
    api.get("/auth/token-info", { params: { token, purpose } }).then((r) => setInfo(r.data))
      .catch((e) => { setInfo(false); setInvalidMsg(formatApiError(e.response?.data?.detail)); });
    return () => meta.remove();
  }, [purpose]);

  const submit = async (e) => {
    e.preventDefault(); setError("");
    if (!passwordValid(pw)) return setError("Adgangskoden opfylder ikke kravene.");
    if (pw !== pw2) return setError("De to adgangskoder er ikke ens.");
    setLoading(true);
    try { await api.post("/auth/set-password", { token: tokenRef.current, purpose, password: pw }); setDone(true); }
    catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  const caption = isInvite ? "Aktivér konto" : "Ny adgangskode";
  const title = isInvite ? "Vælg din adgangskode" : "Vælg en ny adgangskode";

  if (info === null) return <AuthShell caption={caption} title={title}><p className="text-sm text-stone-500" data-testid="setpw-checking">Kontrollerer link…</p></AuthShell>;
  if (info === false) return (
    <AuthShell caption={caption} title="Linket virker ikke">
      <p className="text-sm text-stone-700 rounded-xl border border-[#E9C9C3] bg-[#FDF8F6] p-4" data-testid="setpw-invalid">{invalidMsg}</p>
      <div className="mt-5 flex flex-col gap-2">
        {!isInvite && <Link to="/glemt-adgangskode" className="text-sm text-[#A83232] hover:underline font-medium" data-testid="setpw-request-new">Bed om et nyt link</Link>}
        {isInvite && <p className="text-sm text-stone-500">Kontakt administrator for at få sendt en ny invitation – eller brug <Link to="/glemt-adgangskode" className="text-[#A83232] hover:underline">Glemt adgangskode</Link>.</p>}
        <Link to="/login" className="text-sm text-stone-500 hover:text-stone-900">Tilbage til login</Link>
      </div>
    </AuthShell>
  );
  if (done) return (
    <AuthShell caption={caption} title={isInvite ? "Din konto er aktiveret" : "Adgangskoden er ændret"}>
      <p className="text-sm text-stone-700 rounded-xl border border-[#D3D8C6] bg-[#F3F6EE] p-4" data-testid="setpw-done">{isInvite ? "Du kan nu logge ind med din e-mail og din nye adgangskode." : "Du er logget ud på alle andre enheder. Log ind igen med din nye adgangskode."}</p>
      <Button className="w-full rounded-xl h-11 font-semibold text-[15px] mt-5" onClick={() => nav("/login")} data-testid="setpw-go-login">Gå til login</Button>
    </AuthShell>
  );
  return (
    <AuthShell caption={caption} title={title}>
      <form onSubmit={submit} className="space-y-5">
        <p className="text-sm text-stone-500 -mt-5">{isInvite ? <>Hej {info.name || ""} – du aktiverer kontoen for <strong>{info.email}</strong>.</> : <>Ny adgangskode til <strong>{info.email}</strong>.</>}</p>
        <PasswordField value={pw} onChange={setPw} testid="setpw-password" autoFocus />
        <PasswordField value={pw2} onChange={setPw2} placeholder="Gentag adgangskode" testid="setpw-password2" showRules={false} />
        {error && <p data-testid="setpw-error" className="text-sm text-destructive font-medium">{error}</p>}
        <Button data-testid="setpw-submit" type="submit" disabled={loading} className="w-full rounded-xl h-11 font-semibold text-[15px]">{loading ? "Gemmer…" : (isInvite ? "Aktivér konto" : "Gem ny adgangskode")}</Button>
      </form>
    </AuthShell>
  );
}
